import {
  Box,
  Flex,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import MainModel from "../../../../components/Models/main-model";
import { addVideoQuestionRequest } from "../../../../modules/admin-modules/videos/Actions";

const emptyChoices = () =>
  Array.from({ length: 4 }, () => ({ text: "", is_correct: false }));

const parseExistingQuestion = (raw) => {
  if (!raw) {
    const choices = emptyChoices();
    choices[0].is_correct = true;
    return { question: "", choices };
  }
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const choices = emptyChoices().map((choice, idx) => ({
      text: parsed?.choices?.[idx]?.text || "",
      is_correct: Boolean(parsed?.choices?.[idx]?.is_correct),
    }));
    if (!choices.some((c) => c.is_correct)) {
      choices[0].is_correct = true;
    }
    return {
      question: parsed?.question || "",
      choices,
    };
  } catch {
    return { question: String(raw), choices: emptyChoices() };
  }
};

const parseTime = (raw) => {
  const parts = String(raw || "0:0:0").split(":");
  return {
    h: parseInt(parts[0], 10) || 0,
    m: parseInt(parts[1], 10) || 0,
    s: parseInt(parts[2], 10) || 0,
  };
};

const pad = (n) => String(parseInt(n, 10) || 0).padStart(2, "0");

const AddVideoQuestionModal = ({ isOpen, onClose, video }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.videos.isLoading);
  const [question, setQuestion] = useState("");
  const [choices, setChoices] = useState(emptyChoices());
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const parsed = parseExistingQuestion(video?.question);
    setQuestion(parsed.question);
    setChoices(parsed.choices);
    setTime(parseTime(video?.time));
  }, [isOpen, video]);

  const setCorrectChoice = (index) => {
    setChoices((prev) =>
      prev.map((choice, idx) => ({
        ...choice,
        is_correct: idx === index,
      }))
    );
  };

  const updateChoiceText = (index, text) => {
    setChoices((prev) =>
      prev.map((choice, idx) => (idx === index ? { ...choice, text } : choice))
    );
  };

  const handleSubmit = () => {
    if (!question.trim()) {
      toast.error("أدخل نص السؤال");
      return;
    }
    if (choices.some((c) => !c.text.trim())) {
      toast.error("أدخل الاختيارات الأربعة");
      return;
    }
    if (!choices.some((c) => c.is_correct)) {
      toast.error("اختر الإجابة الصحيحة");
      return;
    }

    const questionPayload = JSON.stringify({
      question: question.trim(),
      choices: choices.map((c) => ({
        text: c.text.trim(),
        is_correct: Boolean(c.is_correct),
      })),
    });

    dispatch(
      addVideoQuestionRequest(
        {
          video_id: video.id,
          question: questionPayload,
          time: `${parseInt(time.h, 10) || 0}:${pad(time.m)}:${pad(time.s)}`,
        },
        onClose
      )
    );
  };

  const header = (
    <Box>
      <Heading textAlign="center" color="main_1" fontSize="24px">
        سؤال داخل الفيديو
      </Heading>
      {video?.title && (
        <Text textAlign="center" fontSize="sm" color="gray.600" mt={1}>
          {video.title}
        </Text>
      )}
    </Box>
  );

  return (
    <MainModel
      header={header}
      closeFunc={onClose}
      openVar={isOpen}
      buttonTitle="حفظ السؤال"
      buttonFunc={handleSubmit}
      isLoading={isLoading}
    >
      <Stack spacing={4} dir="rtl">
        <Box>
          <FormLabel>نص السؤال</FormLabel>
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="اكتب السؤال"
            bg="#f5f5f5"
            borderRadius="12px"
            minH="90px"
          />
        </Box>

        <Box>
          <FormLabel>وقت ظهور السؤال (H:M:S)</FormLabel>
          <Flex gap={2} align="center">
            <Box>
              <Text fontSize="12px" mb={1} textAlign="center">
                H
              </Text>
              <NumberInput
                min={0}
                value={time.h}
                onChange={(_, val) => setTime((t) => ({ ...t, h: val || 0 }))}
                w="80px"
              >
                <NumberInputField bg="#f5f5f5" borderRadius="12px" />
              </NumberInput>
            </Box>
            <Box>
              <Text fontSize="12px" mb={1} textAlign="center">
                M
              </Text>
              <NumberInput
                min={0}
                max={59}
                value={time.m}
                onChange={(_, val) => setTime((t) => ({ ...t, m: val || 0 }))}
                w="80px"
              >
                <NumberInputField bg="#f5f5f5" borderRadius="12px" />
              </NumberInput>
            </Box>
            <Box>
              <Text fontSize="12px" mb={1} textAlign="center">
                S
              </Text>
              <NumberInput
                min={0}
                max={59}
                value={time.s}
                onChange={(_, val) => setTime((t) => ({ ...t, s: val || 0 }))}
                w="80px"
              >
                <NumberInputField bg="#f5f5f5" borderRadius="12px" />
              </NumberInput>
            </Box>
          </Flex>
        </Box>

        <Box>
          <FormLabel>الاختيارات (فعّل المفتاح بجانب الإجابة الصحيحة)</FormLabel>
          <Stack spacing={3}>
            {choices.map((choice, index) => (
              <Flex key={index} gap={3} align="center">
                <Input
                  value={choice.text}
                  onChange={(e) => updateChoiceText(index, e.target.value)}
                  placeholder={`الاختيار ${index + 1}`}
                  bg="#f5f5f5"
                  borderRadius="12px"
                />
                <Switch
                  isChecked={choice.is_correct}
                  onChange={() => setCorrectChoice(index)}
                  colorScheme="green"
                />
              </Flex>
            ))}
          </Stack>
        </Box>
      </Stack>
    </MainModel>
  );
};

export default AddVideoQuestionModal;
